import { ZERO_WEI } from '@kwenta/sdk/constants'
import { PositionSide } from '@kwenta/sdk/types'
import {
	floorNumber,
	formatDollars,
	formatNumber,
	formatPercent,
	stripZeros,
} from '@kwenta/sdk/utils'
import { wei } from '@synthetixio/wei'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import BaseModal from 'components/BaseModal'
import { TransactionButton } from 'components/Button/Button'
import ErrorView from 'components/ErrorView'
import { InfoBoxContainer, InfoBoxRow } from 'components/InfoBox'
import { FlexDivRowCentered } from 'components/layout/flex'
import PreviewArrow from 'components/PreviewArrow'
import SelectorButtons from 'components/SelectorButtons'
import Spacer from 'components/Spacer'
import { Body } from 'components/Text'
import { previewErrorI18n } from 'queries/futures/constants'
import { setShowPositionModal } from 'state/app/reducer'
import {
	editCrossMarginCloseAmount,
	submitCrossMarginReducePositionOrder,
} from 'state/futures/crossMargin/actions'
import {
	selectCloseCMPositionModalInfo,
	selectCloseCMPositionOrderInputs,
	selectCloseCMPositionPreview,
	selectIsFetchingCMTradePreview,
} from 'state/futures/crossMargin/selectors'
import { selectSubmittingFuturesTx } from 'state/futures/selectors'
import { selectTradePreviewError } from 'state/futures/smartMargin/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'

import CrossMarginCloseFees from '../FeeInfoBox/CrossMarginCloseFees'

import ClosePositionSizeInput from './ClosePositionSizeInput'

const CLOSE_PERCENT_OPTIONS = ['25%', '50%', '75%', '100%']

// TODO: Share some logic between close modals

export default function CloseCrossMarginPositionModal() {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()

	const isSubmitting = useAppSelector(selectSubmittingFuturesTx)
	const isFetchingPreview = useAppSelector(selectIsFetchingCMTradePreview)
	const previewTrade = useAppSelector(selectCloseCMPositionPreview)
	const previewError = useAppSelector(selectTradePreviewError)
	const { nativeSizeDelta } = useAppSelector(selectCloseCMPositionOrderInputs)
	const { market, position } = useAppSelector(selectCloseCMPositionModalInfo)

	const submitCloseOrder = useCallback(() => {
		dispatch(submitCrossMarginReducePositionOrder())
	}, [dispatch])

	const isLoading = useMemo(
		() => isSubmitting || isFetchingPreview,
		[isSubmitting, isFetchingPreview]
	)

	const maxNativeValue = useMemo(() => {
		return position?.details.size ?? ZERO_WEI
	}, [position?.details.size])

	const sizeWei = useMemo(
		() => (!nativeSizeDelta || isNaN(Number(nativeSizeDelta)) ? wei(0) : wei(nativeSizeDelta)),
		[nativeSizeDelta]
	)

	const invalidSize = useMemo(() => {
		return sizeWei.abs().gt(maxNativeValue.abs())
	}, [sizeWei, maxNativeValue])

	const orderError = useMemo(() => {
		if (previewError) return t(previewErrorI18n(previewError))
		if (previewTrade?.showStatus) return previewTrade?.statusMessage
		return null
	}, [previewTrade?.showStatus, previewTrade?.statusMessage, previewError, t])

	const submitDisabled = useMemo(() => {
		return sizeWei.eq(0) || invalidSize || isLoading
	}, [sizeWei, invalidSize, isLoading])

	const onClose = () => {
		if (market) {
			dispatch(editCrossMarginCloseAmount(''))
		}
		dispatch(setShowPositionModal(null))
	}

	const onSelectPercent = useCallback(
		(index: number) => {
			if (!position?.details.size || !market?.marketKey) return

			const option = CLOSE_PERCENT_OPTIONS[index]
			const percent = Math.abs(Number(option.replace('%', ''))) / 100
			const size =
				percent === 1
					? position.details.size.abs()
					: floorNumber(position.details.size.abs().mul(percent))

			const sizeDelta = position?.details.side === PositionSide.LONG ? wei(size).neg() : wei(size)
			const decimals = sizeDelta.abs().eq(position.details.size.abs()) ? undefined : 4

			dispatch(editCrossMarginCloseAmount(stripZeros(sizeDelta.toString(decimals))))
		},
		[dispatch, position?.details.size, position?.details.side, market?.marketKey]
	)

	return (
		<StyledBaseModal title="Close full or partial position" isOpen onDismiss={onClose}>
			<Spacer height={10} />

			<ClosePositionSizeInput maxNativeValue={maxNativeValue} />
			<SelectorButtons options={CLOSE_PERCENT_OPTIONS} onSelect={onSelectPercent} />

			<Spacer height={20} />

			<InfoBoxContainer>
				<InfoBoxRow
					boldValue
					title={t('futures.market.trade.edit-position.market')}
					textValue={market?.marketKey}
				/>
				<InfoBoxRow
					textValueIcon={
						previewTrade?.sizeDelta && (
							<PreviewArrow showPreview>
								{previewTrade?.sizeDelta && position?.details
									? formatNumber(position.details.size.sub(previewTrade.sizeDelta.abs()), {
											suggestDecimals: true,
									  })
									: '-'}
							</PreviewArrow>
						)
					}
					title={t('futures.market.trade.edit-position.position-size')}
					textValue={formatNumber(position?.details.size || 0, { suggestDecimals: true })}
				/>
				<InfoBoxRow
					title={t('futures.market.trade.edit-position.price-impact')}
					textValue={formatPercent(previewTrade?.priceImpact || 0, {
						suggestDecimals: true,
						maxDecimals: 4,
					})}
				/>
				<InfoBoxRow
					title={t('futures.market.trade.edit-position.fill-price')}
					textValue={formatDollars(previewTrade?.price || 0, { suggestDecimals: true })}
				/>
			</InfoBoxContainer>

			<Spacer height={20} />

			<TransactionButton
				loading={isLoading}
				variant="flat"
				data-testid="futures-market-trade-deposit-margin-button"
				disabled={submitDisabled}
				fullWidth
				onClick={submitCloseOrder}
			>
				{t('futures.market.trade.edit-position.submit-close')}
			</TransactionButton>

			{orderError && (
				<ErrorView
					containerStyle={{ margin: '16px 0' }}
					messageType={'error'}
					message={orderError}
					formatter="revert"
				/>
			)}
			<Spacer height={20} />
			<CrossMarginCloseFees />
		</StyledBaseModal>
	)
}

export const StyledBaseModal = styled(BaseModal)`
	[data-reach-dialog-content] {
		width: 400px;
	}
`

export const InfoContainer = styled(FlexDivRowCentered)`
	margin: 16px 0;
`

export const BalanceText = styled(Body)`
	color: ${(props) => props.theme.colors.selectedTheme.gray};
	span {
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
`
